/**
 * The pain-point tab strip above the review cards.
 *
 * A real tablist rather than a row of buttons: arrow keys move between tabs,
 * Home and End jump to the ends, and only the selected tab is in the tab order,
 * which is what a screen reader user expects the moment the first tab takes
 * focus.
 *
 * Delegated per section and re-runnable, because the theme editor tears a
 * section down and rebuilds it without reloading the page.
 */
(() => {
  // The loading script tag lives inside the section, so an editor re-render
  // executes this file again. The document-level listeners below must only
  // ever bind once or block:select would click a tab N times.
  if (window.__h7ReviewTabsBound) return;
  window.__h7ReviewTabsBound = true;

  // Matches the max-width: 989px block in h7-review-tabs.css, which is where the
  // panel stops being a three-up grid and becomes a one-card carousel. Below it
  // the arrows step through reviews; above it there is nothing left to step
  // through inside a panel, so they step through the tabs instead.
  const CARD_MODE = '(max-width: 989px)';

  const setupArrows = (root, tabs, panels, select) => {
    const arrows = Array.from(root.querySelectorAll('[data-h7-review-arrow]'));
    if (arrows.length === 0) return;

    const cardsIn = (panel) => Array.from(panel.querySelectorAll('.h7-review-tabs__card'));
    const mostCards = panels.reduce((max, panel) => Math.max(max, cardsIn(panel).length), 0);

    // Nothing to page through in either direction: one tab holding one review.
    if (tabs.length < 2 && mostCards < 2) {
      arrows.forEach((arrow) => {
        arrow.hidden = true;
      });
      return;
    }

    const media = window.matchMedia(CARD_MODE);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');

    const applyLabels = () => {
      arrows.forEach((arrow) => {
        const label = media.matches ? arrow.dataset.labelCard : arrow.dataset.labelTab;
        if (label) arrow.setAttribute('aria-label', label);
      });
    };

    // Whichever card sits nearest the middle of the viewport is the one being
    // read, which is also where a snap point leaves it.
    const centredCard = (panel) => {
      const cards = cardsIn(panel);
      const middle = panel.scrollLeft + panel.clientWidth / 2;
      let index = 0;
      let closest = Infinity;
      cards.forEach((card, position) => {
        const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle);
        if (distance < closest) {
          closest = distance;
          index = position;
        }
      });
      return { cards, index };
    };

    const step = (direction) => {
      if (media.matches) {
        const panel = panels.find((candidate) => !candidate.hidden);
        if (!panel) return;
        const { cards, index } = centredCard(panel);
        if (cards.length === 0) return;
        const target = cards[(index + direction + cards.length) % cards.length];
        panel.scrollTo({
          left: target.offsetLeft - (panel.clientWidth - target.offsetWidth) / 2,
          behavior: still.matches ? 'auto' : 'smooth',
        });
        return;
      }
      const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      const from = current < 0 ? 0 : current;
      select((from + direction + tabs.length) % tabs.length);
    };

    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => step(arrow.dataset.h7ReviewArrow === 'next' ? 1 : -1));
    });

    media.addEventListener('change', applyLabels);
    applyLabels();
  };

  const setup = (root) => {
    if (!root || root.dataset.h7ReviewTabsReady === 'true') return;
    const tabs = Array.from(root.querySelectorAll('[data-h7-review-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-h7-review-panel]'));
    if (tabs.length === 0 || tabs.length !== panels.length) return;

    root.dataset.h7ReviewTabsReady = 'true';

    const select = (index, { focus = false } = {}) => {
      tabs.forEach((tab, position) => {
        const isSelected = position === index;
        tab.classList.toggle('is-selected', isSelected);
        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        // Roving tabindex: one stop for the whole strip, then arrow keys.
        tab.tabIndex = isSelected ? 0 : -1;
        panels[position].hidden = !isSelected;
      });
      // A panel left mid-swipe would otherwise reopen on the card the shopper
      // happened to stop on, three tabs ago.
      panels[index].scrollLeft = 0;
      if (focus) tabs[index].focus();
    };

    setupArrows(root, tabs, panels, select);

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index));
      tab.addEventListener('keydown', (event) => {
        const last = tabs.length - 1;
        let next = null;
        if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
        else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        select(next, { focus: true });
      });
    });
  };

  const setupAll = (scope) => {
    if (!scope) return;
    if (scope.matches?.('[data-h7-review-tabs]')) setup(scope);
    scope.querySelectorAll?.('[data-h7-review-tabs]').forEach(setup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupAll(document), { once: true });
  } else {
    setupAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest('[data-h7-review-panel]');
    const root = event.target.closest('[data-h7-review-tabs]');
    if (!panel || !root) return;
    const panels = Array.from(root.querySelectorAll('[data-h7-review-panel]'));
    const tabs = Array.from(root.querySelectorAll('[data-h7-review-tab]'));
    const index = panels.indexOf(panel);
    if (index < 0 || !tabs[index]) return;
    tabs[index].click();
  });
})();
